import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { Reveal } from "../components/common";
import { publicApi, mediaUrl } from "../lib/api";
import Seo from "../components/Seo";

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState("All");
  useEffect(() => { publicApi.blog().then(setPosts).catch(() => {}); }, []);

  const categories = ["All", ...Array.from(new Set(posts.map((p) => p.category)))];
  const filtered = active === "All" ? posts : posts.filter((p) => p.category === active);
  const featured = posts[0];

  return (
    <div>
      <Seo title="Blog — Business, Tax & Cloud Accounting Insights" description="Insights, guides and updates on tax, VAT, payroll, cash flow, outsourced finance and running a healthy, growing business from the CASS team." />
      <section className="numbers-pattern text-white">
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
          <span className="text-orange font-bold text-sm tracking-widest uppercase">More Than Numbers</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl uppercase leading-[0.95] mt-3">The Blog</h1>
          <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">Insights, guides and updates on tax, cloud accounting and running a healthy business.</p>
        </div>
      </section>

      {featured && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <Reveal>
              <Link to={`/blog/${featured.slug}`} className="group grid md:grid-cols-2 gap-8 items-center bg-slate-50 rounded-3xl overflow-hidden border border-slate-100">
                <div className="aspect-[4/3] md:aspect-auto md:h-full overflow-hidden bg-slate-50"><img loading="lazy" decoding="async" src={mediaUrl(featured.image, 1200)} alt={featured.image_alt || featured.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-8 md:p-10">
                  <span className="inline-block bg-orange/10 text-orange text-xs font-bold px-3 py-1 rounded-full mb-4">{featured.category}</span>
                  <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy leading-tight mb-4 group-hover:text-orange transition-colors">{featured.title}</h2>
                  <p className="text-slate-500 leading-relaxed mb-5">{featured.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1.5"><Calendar size={13} /> {formatDate(featured.date)}</span>
                    <span className="flex items-center gap-1.5"><Clock size={13} /> {featured.readTime}</span>
                  </div>
                </div>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      <section className="pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((c) => (
              <button key={c} onClick={() => setActive(c)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${active === c ? "bg-navy text-white" : "bg-slate-100 text-navy hover:bg-slate-200"}`}>{c}</button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={i * 60}>
                <Link to={`/blog/${p.slug}`} className="group block h-full bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300">
                  <div className="aspect-[16/10] overflow-hidden bg-white"><img loading="lazy" decoding="async" src={mediaUrl(p.image, 800)} alt={p.image_alt || p.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-6">
                    <span className="inline-block bg-orange/10 text-orange text-xs font-bold px-3 py-1 rounded-full mb-3">{p.category}</span>
                    <h3 className="font-display font-bold text-lg text-navy leading-snug mb-2 group-hover:text-orange transition-colors">{p.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-4 line-clamp-2">{p.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span className="flex items-center gap-1.5"><Calendar size={12} /> {formatDate(p.date)}</span>
                      <span className="text-orange font-bold inline-flex items-center gap-1 group-hover:gap-2 transition-all">Read <ArrowRight size={13} /></span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
